/**
 * Modal de **alta/edición de atendido** (`POST /atendidos` / `PATCH /atendidos/<id>`).
 *
 * Réplica de `add_atendido.html`/`edit_atendido.html` del SSR (`progress/
 * explore_admin_ssr.md`): nombre*, teléfono, email y, solo en edición, el toggle
 * `activo`. Valida con Zod antes de delegar y traduce el error de la API a mensaje
 * legible. Controlado por el padre (`target`). Presentacional + hooks; sin `fetch`.
 *
 * Sede: a diferencia del SSR (cuyo formulario llevaba un `sede_id`), la API
 * headless NO acepta la sede en el cuerpo: el atendido pertenece a la **sede
 * activa** que el cliente envía por `X-Sede-Id` (`domains/atendidos.py`), y la API
 * no mueve atendidos entre sedes. Por eso aquí la sede no es un campo editable: se
 * MUESTRA la sede activa (de `useAuth().activeSede`) como contexto de dónde se
 * creará el atendido, coherente con el selector de sede del encabezado.
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { adminErrorMessage } from "../format";
import {
  useCreateAtendido,
  useUpdateAtendido,
} from "../hooks/useAtendidoMutations";
import {
  createAtendidoSchema,
  editAtendidoSchema,
  type CreateAtendidoValues,
  type EditAtendidoValues,
} from "../schemas";
import type {
  AtendidoListItem,
  CreateAtendidoBody,
  UpdateAtendidoBody,
} from "../types";

interface AtendidoFormDialogProps {
  /** `null` mantiene el modal cerrado. En `edit`, el atendido a editar. */
  target: { mode: "create" } | { mode: "edit"; atendido: AtendidoListItem } | null;
  onClose: () => void;
}

/** Errores por campo (de Zod). */
type FieldErrors = Partial<Record<"nombre" | "telefono" | "email", string>>;

interface FormState {
  nombre: string;
  telefono: string;
  email: string;
  activo: boolean;
}

const EMPTY_STATE: FormState = {
  nombre: "",
  telefono: "",
  email: "",
  activo: true,
};

export function AtendidoFormDialog({ target, onClose }: AtendidoFormDialogProps) {
  const { activeSede } = useAuth();
  const createAtendido = useCreateAtendido();
  const updateAtendido = useUpdateAtendido();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isEdit = target?.mode === "edit";

  // Al abrir/cambiar de objetivo, hidrata el formulario y limpia el estado de
  // mutación. En edición solo hay datos del listado (sin contacto): se piden los
  // de contacto solo si se quieren editar; el PATCH parcial conserva lo omitido.
  useEffect(() => {
    if (target === null) {
      return;
    }
    if (target.mode === "edit") {
      setForm({
        nombre: target.atendido.nombre,
        telefono: "",
        email: "",
        activo: target.atendido.activo,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setFieldErrors({});
    createAtendido.reset();
    updateAtendido.reset();
    // Solo nos interesa reaccionar al cambio de objetivo (las mutaciones son
    // estables por render de TanStack Query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function collectErrors(
    fieldErrorsObj: Partial<Record<keyof FormState, string[] | undefined>>,
  ): FieldErrors {
    const errors: FieldErrors = {};
    (Object.keys(fieldErrorsObj) as (keyof FormState)[]).forEach((key) => {
      if (key === "activo") {
        return;
      }
      const message = fieldErrorsObj[key]?.[0];
      if (message) {
        errors[key] = message;
      }
    });
    return errors;
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (target === null) {
      return;
    }

    if (target.mode === "create") {
      const result = createAtendidoSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitCreate(result.data);
    } else {
      const result = editAtendidoSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitEdit(target.atendido.id, result.data);
    }
  }

  function submitCreate(values: CreateAtendidoValues) {
    const body: CreateAtendidoBody = { nombre: values.nombre };
    if (values.telefono) {
      body.telefono = values.telefono;
    }
    if (values.email) {
      body.email = values.email;
    }
    createAtendido.mutate(body, { onSuccess: onClose });
  }

  function submitEdit(atendidoId: number, values: EditAtendidoValues) {
    // PATCH parcial: nombre/activo siempre (el form los tiene); los datos de
    // contacto solo si se escribieron (vacío = no tocar, el backend conserva).
    const body: UpdateAtendidoBody = {
      nombre: values.nombre,
      activo: values.activo,
    };
    if (values.telefono) {
      body.telefono = values.telefono;
    }
    if (values.email) {
      body.email = values.email;
    }
    updateAtendido.mutate({ atendidoId, body }, { onSuccess: onClose });
  }

  const activeMutation = isEdit ? updateAtendido : createAtendido;
  const apiError = activeMutation.isError
    ? adminErrorMessage(activeMutation.error)
    : null;
  const isSubmitting = activeMutation.isPending;

  // Nombre de la sede a la que pertenece el atendido: en edición, la suya; en
  // alta, la sede activa (a donde lo creará la API vía X-Sede-Id).
  const sedeName =
    isEdit && target?.mode === "edit"
      ? (target.atendido.sede?.nombre ?? "—")
      : (activeSede?.nombre ?? "—");

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>{isEdit ? "Editar atendido" : "Nuevo atendido"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Modifica los datos del atendido. Deja los datos de contacto en blanco para conservarlos."
            : "Completa los datos para registrar un nuevo atendido en la sede activa."}
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-700">Sede</span>
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {sedeName}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="atendido-nombre" className="text-sm font-medium text-slate-700">
              Nombre
            </label>
            <Input
              id="atendido-nombre"
              autoComplete="off"
              value={form.nombre}
              aria-invalid={fieldErrors.nombre ? true : undefined}
              aria-describedby={fieldErrors.nombre ? "atendido-nombre-error" : undefined}
              onChange={(event) => setField("nombre", event.target.value)}
            />
            {fieldErrors.nombre && (
              <p id="atendido-nombre-error" className="text-sm text-red-600">
                {fieldErrors.nombre}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="atendido-telefono" className="text-sm font-medium text-slate-700">
              Teléfono{isEdit ? " (opcional)" : ""}
            </label>
            <Input
              id="atendido-telefono"
              autoComplete="off"
              value={form.telefono}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : undefined}
              onChange={(event) => setField("telefono", event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="atendido-email" className="text-sm font-medium text-slate-700">
              Email{isEdit ? " (opcional)" : ""}
            </label>
            <Input
              id="atendido-email"
              type="email"
              autoComplete="off"
              value={form.email}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : undefined}
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={fieldErrors.email ? "atendido-email-error" : undefined}
              onChange={(event) => setField("email", event.target.value)}
            />
            {fieldErrors.email && (
              <p id="atendido-email-error" className="text-sm text-red-600">
                {fieldErrors.email}
              </p>
            )}
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                checked={form.activo}
                onChange={(event) => setField("activo", event.target.checked)}
              />
              Atendido activo
            </label>
          )}

          {apiError && (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Guardando…" : isEdit ? "Actualizar" : "Crear atendido"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
